import React from "react";
import { Alert, Table, Tag } from "antd";

/**
 * Qué libros va a vender este archivo y si alcanzan los códigos.
 *
 * Los libros no se eligen aquí: salen de la Lista Escolar de cada alumno. Este panel sólo
 * resume lo que quedó y avisa de los dos problemas que si no se ven aquí se ven hasta el
 * final: que falten códigos en el almacén, o que el insumo no tenga código del SAT (que
 * hace que `agregarVenta` rechace la venta completa).
 */
const ResumenLibros = ({ libros, insumos, disponiblesPorInsumo, proyectoId }) => {
  if (!libros || libros.length === 0) return null;

  const insumoDe = (insumoId) =>
    (insumos || []).find((insumo) => String(insumo._id) === String(insumoId));

  const disponibleDe = (insumoId) => {
    if (!proyectoId) return null;
    const encontrado = (disponiblesPorInsumo || []).find(
      (valueDisp) =>
        String(valueDisp.insumoId) === String(insumoId) &&
        String(valueDisp.proyectoId) === String(proyectoId),
    );
    return encontrado ? encontrado.disponible : 0;
  };

  const datos = libros.map((libro) => {
    const insumo = insumoDe(libro.insumoId);
    return {
      key: String(libro.insumoId),
      descripcion: insumo
        ? (insumo.clave ? insumo.clave + " - " : "") + insumo.descripcion
        : "(insumo no encontrado)",
      sinCodigoSat: insumo ? !insumo.codigoSatId : false,
      necesarios: libro.cantidad,
      alumnos: libro.alumnos,
      disponible: disponibleDe(libro.insumoId),
    };
  });

  const faltantes = datos.filter(
    (fila) => fila.disponible !== null && fila.disponible < fila.necesarios,
  );
  const sinSat = datos.filter((fila) => fila.sinCodigoSat);

  const columnas = [
    { title: "Libro", dataIndex: "descripcion", key: "descripcion", width: 320 },
    { title: "Alumnos", dataIndex: "alumnos", key: "alumnos", width: 90, align: "center" },
    {
      title: "Códigos necesarios",
      dataIndex: "necesarios",
      key: "necesarios",
      width: 140,
      align: "center",
    },
    {
      title: "Disponibles",
      key: "disponible",
      width: 140,
      align: "center",
      render: (_, record) => {
        if (record.disponible === null) return <Tag>Elige proyecto</Tag>;
        return (
          <Tag color={record.disponible >= record.necesarios ? "green" : "orange"}>
            {record.disponible}
          </Tag>
        );
      },
    },
    {
      title: "Código SAT",
      key: "codigoSat",
      width: 120,
      align: "center",
      render: (_, record) =>
        record.sinCodigoSat ? <Tag color="red">Falta</Tag> : <Tag color="green">Sí</Tag>,
    },
  ];

  return (
    <div style={{ marginBottom: 12 }}>
      {sinSat.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 8 }}
          message="Hay libros sin código del SAT"
          description="Sin código del SAT no se puede registrar la venta. Asígnalo en el insumo antes de importar."
        />
      )}
      {sinSat.length === 0 && faltantes.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="No alcanzan los códigos en este proyecto"
          description="Las ventas se crearán de todos modos, pero las que se queden sin código habrá que entregarlas a mano después. Conviene cargar los códigos faltantes antes de importar."
        />
      )}
      {sinSat.length === 0 && faltantes.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="Libros que se van a vender"
          description="Salen de la Lista Escolar de cada alumno, no del texto del formulario."
        />
      )}
      <Table
        size="small"
        pagination={false}
        dataSource={datos}
        columns={columnas}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default ResumenLibros;
